import { useFormContext } from 'react-hook-form';
import { logError } from '@dx-ui/framework-logger';
import type { AddressFieldsOptions } from '../address-fields';
import type { DXError } from '@dx-ui/types-graphql';
import { useGeocodeQuery, serverSideGeocodeQuery } from '../gql/queries';
import { generateError } from '@dx-ui/utilities-generate-error';
import { useTranslation } from 'next-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

type GeocodeErrorLabels = {
  cityErrorLabel: string;
  postalCodeErrorLabel: string;
  stateErrorLabel: string;
};

export const useHelpers = (options: AddressFieldsOptions) => {
  const { t } = useTranslation('osc-address-fields');
  const formContext = useFormContext();
  if (!formContext)
    throw new Error('You cannot use this helper generator outside of a React Hook Form context');

  function getFieldKey(field: string) {
    return `${options.fieldNamePrefix}.${field}`;
  }

  function setStateToDefaultOption() {
    formContext.setValue(`${options.fieldNamePrefix}.state`, '');
  }

  function setCityToEmpty() {
    formContext.setValue(`${options.fieldNamePrefix}.city`, '');
  }

  function clearAddressFieldErrors(fields: string[] = []) {
    fields.forEach((field) => formContext.clearErrors(`${options.fieldNamePrefix}.${field}`));
  }

  function setCityStateOnGeocodeFailure(labels: GeocodeErrorLabels) {
    setCityToEmpty();
    setStateToDefaultOption();
    formContext.setError(getFieldKey('postalCode'), {
      type: 'validate',
      message:
        options?.addressType === 'nonBusinessShortened'
          ? t('postalCode.error_lookup_shortenEnrollment', { ...labels })
          : t('postalCode.error_lookup', { ...labels }),
    });
  }

  function handleGeocodeError(error: DXError, labels: GeocodeErrorLabels) {
    const errors = JSON.parse(JSON.stringify(error));
    for (const error of errors.graphQLErrors) {
      if (error?.message === 'Not Found') {
        setCityStateOnGeocodeFailure(labels);
        if (error instanceof Error) {
          logError(
            'Fetch Error on GeoCode Query',
            generateError(error?.message),
            `Error on ${useGeocodeQuery.name} at libs/osc/address-fields`
          );
        }
      }
    }
  }

  function isValidCountryCode(code: string) {
    return /^\w{2}$/.test(code);
  }

  function isValidPostalCode(regexValidation: string, postalCode: string) {
    const pattern =
      regexValidation.startsWith('/') && regexValidation.endsWith('/')
        ? regexValidation.slice(1, -1)
        : regexValidation;

    return postalCode.match(pattern);
  }

  function useFetchGeocodeQuery(language: string = 'en') {
    const client = useQueryClient();
    return useCallback(
      function fetchGeocodeQuery({ postalCode }: { postalCode: string }) {
        return serverSideGeocodeQuery(client, {
          postalCode,
          language,
        });
      },
      [client, language]
    );
  }

  return {
    getFieldKey,
    handleGeocodeError,
    setStateToDefaultOption,
    setCityToEmpty,
    clearAddressFieldErrors,
    isValidCountryCode,
    isValidPostalCode,
    useFetchGeocodeQuery,
    setCityStateOnGeocodeFailure,
  };
};
